import axios from "axios";
import ApiConfig from "../config/api-config";

const config = {
  baseURL: ApiConfig.BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};
const configForm = {
  baseURL: ApiConfig.BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data;",
  },
};

const normalizeLocale = (locale) => {
  if (locale === "uk") {
    return "ua";
  }

  return locale || "ua";
};

const getLocaleFromStorage = () => {
  return normalizeLocale(localStorage.getItem("locale"));
};
const $apiClient = axios.create(config);
const $apiClientForm = axios.create(configForm);

const pendingGetRequests = new Map();

const stableStringify = (value) => {
  if (!value || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

const getHeaderValue = (headers, headerName) => {
  if (!headers) {
    return undefined;
  }

  if (typeof headers.get === "function") {
    return headers.get(headerName);
  }

  const normalizedHeaderName = headerName.toLowerCase();
  const matchedHeader = Object.keys(headers).find(
    (key) => key.toLowerCase() === normalizedHeaderName
  );

  return matchedHeader ? headers[matchedHeader] : undefined;
};

const createGetRequestKey = (instance, url, requestConfig = {}) => {
  const headers = requestConfig.headers || {};
  const locale = getHeaderValue(headers, "X-localization") || getLocaleFromStorage();
  const authorization =
    getHeaderValue(headers, "Authorization") || instance.defaults.headers.Authorization || localStorage.getItem("token") || "";

  return stableStringify({
    method: "get",
    baseURL: requestConfig.baseURL || instance.defaults.baseURL,
    url,
    params: requestConfig.params || null,
    locale,
    authorization,
  });
};

const withGetRequestDedupe = (instance) => {
  const originalGet = instance.get.bind(instance);

  instance.get = (url, requestConfig = {}) => {
    const requestKey = createGetRequestKey(instance, url, requestConfig);

    if (pendingGetRequests.has(requestKey)) {
      return pendingGetRequests.get(requestKey);
    }

    const request = originalGet(url, requestConfig).finally(() => {
      pendingGetRequests.delete(requestKey);
    });

    pendingGetRequests.set(requestKey, request);

    return request;
  };
};

// --- Token refresh logic ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshToken = () => {
  const currentToken = localStorage.getItem("token");

  return axios
    .post(`${ApiConfig.BASE_URL}${ApiConfig.REFRESH}`, null, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(currentToken
          ? { Authorization: `Bearer ${currentToken}` }
          : {}),
      },
    })
    .then((response) => {
      const newToken = response.data?.access_token;
      if (!newToken) throw new Error("No access_token in refresh response");
      localStorage.setItem("token", newToken);
      $apiClient.defaults.headers.Authorization = `Bearer ${newToken}`;
      $apiClientForm.defaults.headers.Authorization = `Bearer ${newToken}`;
      return newToken;
    });
};

const addResponseInterceptorWithRefresh = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = error.config;

      if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes(ApiConfig.REFRESH)
      ) {
        if (isRefreshing) {
          // Додаємо запит у чергу і чекаємо завершення refresh
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            originalRequest._retry = true;
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return refreshToken()
          .then((token) => {
            processQueue(null, token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          })
          .catch((refreshError) => {
            processQueue(refreshError, null);
            localStorage.clear();
            window.location.href = "/";
            return Promise.reject(refreshError);
          })
          .finally(() => {
            isRefreshing = false;
          });
      }

      return Promise.reject(error);
    }
  );
};

$apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const locale = getLocaleFromStorage();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-localization"] = locale;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
$apiClientForm.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const locale = getLocaleFromStorage();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-localization"] = locale;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

addResponseInterceptorWithRefresh($apiClient);
addResponseInterceptorWithRefresh($apiClientForm);

withGetRequestDedupe($apiClient);
withGetRequestDedupe($apiClientForm);

export { $apiClient ,$apiClientForm, getLocaleFromStorage, normalizeLocale };
